import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const ProducerBalancesTable = ({ producers }) => {
  const { t } = useTranslation("financials");
  const navigate = useNavigate();
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpand = (userId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            textTransform: "uppercase",
            fontWeight: 500,
            fontSize: "0.875rem",
            color: "text.secondary",
            mb: 2,
          }}
        >
          {t("producerBalances.title")}
        </Typography>

        {!producers || producers.length === 0 ? (
          <Typography color="text.secondary">
            {t("producerBalances.noProducers")}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell>{t("producerBalances.columns.producer")}</TableCell>
                <TableCell>{t("producerBalances.columns.role")}</TableCell>
                <TableCell align="right">
                  {t("producerBalances.columns.earned")}
                </TableCell>
                <TableCell align="right">
                  {t("producerBalances.columns.paid")}
                </TableCell>
                <TableCell align="right">
                  {t("producerBalances.columns.remaining")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {producers.map((p) => {
                const isExpanded = expandedIds.has(p.user_id);
                const hasAssets = p.assets && p.assets.length > 0;

                return (
                  <React.Fragment key={p.user_id}>
                    <TableRow>
                      <TableCell sx={{ width: 48, py: 0.5 }}>
                        {hasAssets && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(p.user_id);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronUp size={16} />
                            ) : (
                              <ChevronDown size={16} />
                            )}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() =>
                          navigate(`/financials/producer/${p.user_id}`)
                        }
                      >
                        {p.user_name}
                      </TableCell>
                      <TableCell>
                        <Chip label={p.role} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        ${p.total_earnings?.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        ${p.total_paid?.toFixed(2)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color:
                            p.remaining > 0 ? "warning.main" : "success.main",
                        }}
                      >
                        ${p.remaining?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}
                      >
                        <Collapse in={isExpanded} unmountOnExit>
                          <Box
                            sx={{
                              m: 1,
                              ml: 6,
                              backgroundColor: "action.hover",
                              borderRadius: 1,
                              p: 1,
                            }}
                          >
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>
                                    {t("revenueByTitle.columns.title")}
                                  </TableCell>
                                  <TableCell align="right">
                                    {t("producerBalances.columns.earned")}
                                  </TableCell>
                                  <TableCell align="right">
                                    {t("producerBalances.columns.paid")}
                                  </TableCell>
                                  <TableCell align="right">
                                    {t("producerBalances.columns.remaining")}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {p.assets.map((a) => (
                                  <TableRow key={a.asset_id}>
                                    <TableCell>{a.asset_title}</TableCell>
                                    <TableCell align="right">
                                      ${a.total_earnings?.toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">
                                      ${a.total_paid?.toFixed(2)}
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        fontWeight: 600,
                                        color:
                                          a.remaining > 0
                                            ? "warning.main"
                                            : "success.main",
                                      }}
                                    >
                                      ${a.remaining?.toFixed(2)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default ProducerBalancesTable;
